import { isEmpty } from '@medplum/core'
import type { MedicationRequest, Patient, Reference } from '@medplum/fhirtypes'
import { type DateString, toDateString } from '@seen/core/dates'
import * as appointmentsLib from '@seen/core/models/appointments/index'
import { Assessment } from '@seen/core/models/assessments/assessment'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { ExtendedCommunication } from '@seen/core/models/communications/ExtendedCommunication'
import { PhoneCommunication } from '@seen/core/models/communications/PhoneCommunication'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { FacilityEncounter } from '@seen/core/models/encounters/facility-encounter'
import { ExtendedObservation } from '@seen/core/models/observation'
import { Order } from '@seen/core/models/orders/Order'
import { ParticipantNote } from '@seen/core/models/participantNote'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { VisitNote } from '@seen/core/models/visitNote'
import { getReferenceString } from '@seen/core/references'
import { now } from '@seen/core/utils'
import { startOfToday } from 'date-fns'
import { groupBy, orderBy } from 'lodash-es'
import { useMemo } from 'react'
import { useFhirSearch } from '#src/hooks/useFhirSearch.ts'
import { useLabCopySecondaryFilter } from '#src/hooks/useLabCopySecondaryFilter.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export type TimelineItem = {
  key: string
  date: Date
} & (
  | {
      type: 'validatedTool'
      data: ValidatedToolResponse
    }
  | {
      type: 'visitNote'
      data: VisitNote | ParticipantNote
    }
  | {
      type: 'assessment'
      data: Assessment
    }
  | {
      type: 'timelineNote'
      data: CommentCommunication
    }
  | {
      type: 'phoneCall'
      data: PhoneCommunication
    }
  | {
      type: 'fax'
      data: ExtendedCommunication
    }
  | {
      type: 'order'
      data: Order
    }
  | {
      type: 'task'
      data: StandardTask
    }
  | {
      type: 'recurringAppointment'
      data: appointmentsLib.ExtendedAppointment[]
    }
  | {
      type: 'appointment'
      data: appointmentsLib.ExtendedAppointment
    }
  | {
      type: 'medicationRequest'
      data: MedicationRequest
    }
  | {
      type: 'documentReference'
      data: ExtendedDocumentReference
    }
  | {
      type: 'groupedDocumentReferences'
      data: ExtendedDocumentReference[]
    }
  | {
      type: 'dayCenterDayNote'
      data: ExtendedObservation
    }
  | {
      type: 'facilityEncounter'
      data: FacilityEncounter
    }
)

export type TimelineItemType = TimelineItem['type']

export type TimelineOptions = {
  includeTypes?: TimelineItemType[]
}

export type TimelineItemGroups = {
  dateString: DateString
  items: TimelineItem[]
}[]

function groupSequentialDocumentReferences(
  items: TimelineItem[]
): TimelineItem[] {
  // Sort items by date (descending) to process in chronological order
  const sortedItems = orderBy(items, [(item) => item.date], ['desc'])

  const result: TimelineItem[] = []
  let currentGroup: ExtendedDocumentReference[] = []
  let lastDocDate: Date | null = null

  const GROUPING_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes

  for (const item of sortedItems) {
    if (item.type === 'documentReference') {
      const docDate = new Date(item.data.date)

      // Check if this document should be grouped with the previous ones
      if (
        currentGroup.length > 0 &&
        lastDocDate &&
        Math.abs(lastDocDate.getTime() - docDate.getTime()) <=
          GROUPING_THRESHOLD_MS
      ) {
        // Add to current group
        currentGroup.push(item.data)
        lastDocDate = docDate
      } else {
        // Finish previous group if it exists
        if (currentGroup.length > 1) {
          // Create grouped item
          result.push({
            key: currentGroup.map((doc) => doc.reference.reference).join(','),
            type: 'groupedDocumentReferences',
            date: new Date(currentGroup[0].date), // Use the most recent date
            data: currentGroup,
          })
        } else if (currentGroup.length === 1) {
          // Single item, keep it as is
          result.push({
            key: currentGroup[0].reference.reference,
            type: 'documentReference',
            date: new Date(currentGroup[0].date),
            data: currentGroup[0],
          })
        }

        // Start new group
        currentGroup = [item.data]
        lastDocDate = docDate
      }
    } else {
      // Finish any pending group
      if (currentGroup.length > 1) {
        result.push({
          key: currentGroup.map((doc) => doc.reference.reference).join(','),
          type: 'groupedDocumentReferences',
          date: new Date(currentGroup[0].date),
          data: currentGroup,
        })
      } else if (currentGroup.length === 1) {
        result.push({
          key: currentGroup[0].reference.reference,
          type: 'documentReference',
          date: new Date(currentGroup[0].date),
          data: currentGroup[0],
        })
      }
      currentGroup = []
      lastDocDate = null

      // Add non-document item
      result.push(item)
    }
  }

  // Handle final group
  if (currentGroup.length > 1) {
    result.push({
      key: currentGroup.map((doc) => doc.reference.reference).join(','),
      type: 'groupedDocumentReferences',
      date: new Date(currentGroup[0].date),
      data: currentGroup,
    })
  } else if (currentGroup.length === 1) {
    result.push({
      key: currentGroup[0].reference.reference,
      type: 'documentReference',
      date: new Date(currentGroup[0].date),
      data: currentGroup[0],
    })
  }

  return result
}

export function filterTimelineItemsByType(
  timelineItems: TimelineItem[],
  options?: TimelineOptions
): TimelineItem[] {
  if (!options?.includeTypes) {
    return timelineItems
  }

  return timelineItems.filter((item) =>
    options.includeTypes?.includes(item.type)
  )
}

interface UseTimelineItemsResult {
  timelineItems: TimelineItem[]
  timelineItemGroups: TimelineItemGroups
  isLoading: boolean
  isEmpty: boolean
}

export function useTimelineItems(
  participantReference: Reference<Patient>,
  options?: TimelineOptions
): UseTimelineItemsResult {
  const withoutLabCopySecondaries = useLabCopySecondaryFilter()
  const { data: validatedToolResponses = [], isLoading: isLoadingTools } =
    useSearch(
      ValidatedToolResponse,
      ValidatedToolResponse.byParticipant({
        participant: participantReference,
      })
    )

  const visitNoteTimelineItems = useVisitNoteTimelineItems(participantReference)

  const { data: assessments = [], isLoading: isLoadingAssessments } = useSearch(
    Assessment,
    Assessment.searchQuery({
      subject: participantReference,
    })
  )

  const { data: timelineNotes = [], isLoading: isLoadingTimelineNotes } =
    useSearch(
      CommentCommunication,
      CommentCommunication.queryTimelineComments(participantReference)
    )

  const { data: phoneCalls = [], isLoading: isLoadingPhoneCalls } = useSearch(
    PhoneCommunication,
    PhoneCommunication.searchQuery([participantReference])
  )

  const { data: faxCommunications = [], isLoading: isLoadingFaxes } = useSearch(
    ExtendedCommunication,
    ExtendedCommunication.faxCommunicationsQuery(participantReference)
  )

  const { data: orders = [], isLoading: isLoadingOrders } = useSearch(
    Order,
    Order.byParticipant({ participant: participantReference })
  )

  const { data: medicationRequests = [], isLoading: isLoadingMedications } =
    useFhirSearch('MedicationRequest', {
      patient: participantReference,
      _sort: '-_lastUpdated',
      _count: '100',
    })

  const { data: standardTasks = [], isLoading: isLoadingTasks } = useSearch(
    StandardTask,
    StandardTask.byParticipant(participantReference)
  )

  const { data: appointments = [], isLoading: isLoadingAppointments } =
    useSearch(
      appointmentsLib.ExtendedAppointment,
      appointmentsLib.ExtendedAppointment.byStatuses({
        patient: participantReference,
        statuses: ['booked', 'cancelled'],
      })
    )

  // We want to group recurring appointments since they're booked at the same time.
  const recurringBookedAppointments = useMemo(() => {
    const groups = groupBy(
      appointments.filter(
        (appointment) =>
          appointment.recurrenceGroupId && appointment.status === 'booked'
      ),
      (appointment) => appointment.recurrenceGroupId
    )
    return Object.values(groups)
  }, [appointments])
  const recurringCancelledAppointments = useMemo(() => {
    const groups = groupBy(
      appointments.filter(
        (appointment) =>
          appointment.recurrenceGroupId && appointment.status === 'cancelled'
      ),
      (appointment) => appointment.recurrenceGroupId
    )
    return Object.values(groups)
  }, [appointments])

  const otherAppointments = appointments.filter(
    (appointment) => !appointment.recurrenceInstanceLink
  )

  const {
    data: documentReferences = [],
    isLoading: isLoadingDocumentReferences,
  } = useSearch(
    ExtendedDocumentReference,
    withoutLabCopySecondaries(
      ExtendedDocumentReference.byParticipant({
        participant: participantReference,
      })
    )
  )

  const {
    data: dayCenterDayNotes = [],
    isLoading: isLoadingDayCenterDayNotes,
  } = useSearch(
    ExtendedObservation,
    ExtendedObservation.searchQuery({
      subject: participantReference,
      code: 'day-center-day-note',
      date: `le${startOfToday().toISOString()}`, // We don't want to show updates to day center notes that are about the future.
    })
  )

  const {
    data: facilityEncounters = [],
    isLoading: isLoadingFacilityEncounters,
  } = useSearch(
    FacilityEncounter,
    FacilityEncounter.searchByPatient(participantReference)
  )

  const timelineItems = useMemo<TimelineItem[]>(() => {
    const allItems: TimelineItem[] = [
      ...validatedToolResponses.map((response): TimelineItem => ({
        key: response.reference.reference,
        type: 'validatedTool',
        date: new Date(response.authored),
        data: response,
      })),
      ...visitNoteTimelineItems,
      ...assessments.map((assessment): TimelineItem => ({
        key: assessment.reference.reference,
        type: 'assessment',
        date: new Date(assessment.signedOn ?? assessment.date),
        data: assessment,
      })),
      ...timelineNotes.map((note): TimelineItem => ({
        key: note.reference.reference,
        type: 'timelineNote',
        date: note.sentDate ?? note.lastUpdatedDate,
        data: note,
      })),
      ...phoneCalls.map((call): TimelineItem => ({
        key: call.reference.reference,
        type: 'phoneCall',
        date: new Date(call.startTime ?? call.lastUpdated),
        data: call,
      })),
      ...faxCommunications.map((fax): TimelineItem => ({
        key: fax.reference.reference,
        type: 'fax',
        date: fax.sentDate ?? fax.elationLetterSentDate ?? fax.lastUpdatedDate,
        data: fax,
      })),
      ...orders.map((order): TimelineItem => ({
        key: order.reference.reference,
        type: 'order',
        date: order.date ?? new Date(),
        data: order,
      })),
      ...medicationRequests.map((request): TimelineItem => ({
        key: getReferenceString(request),
        type: 'medicationRequest',
        date: new Date(
          request.authoredOn ?? request.meta?.lastUpdated ?? now()
        ),
        data: request,
      })),
      ...standardTasks.map((task): TimelineItem => ({
        key: task.reference.reference,
        type: 'task',
        date: task.lastModifiedDate ?? task.lastUpdatedDate ?? new Date(),
        data: task,
      })),
      ...recurringBookedAppointments.map((appointments): TimelineItem => ({
        key: appointments[0].reference.reference,
        type: 'recurringAppointment',
        date: appointments[0].lastUpdatedDate,
        data: appointments,
      })),
      ...recurringCancelledAppointments.map((appointments): TimelineItem => ({
        key: appointments[0].reference.reference,
        type: 'recurringAppointment',
        date: appointments[0].lastUpdatedDate,
        data: appointments,
      })),
      ...otherAppointments.map((appointment): TimelineItem => ({
        key: appointment.reference.reference,
        type: 'appointment',
        date: appointment.lastUpdatedDate,
        data: appointment,
      })),
      ...documentReferences.map((doc): TimelineItem => ({
        key: doc.reference.reference,
        type: 'documentReference',
        date: new Date(doc.date),
        data: doc,
      })),
      ...dayCenterDayNotes.map((observation): TimelineItem => ({
        key: observation.reference.reference,
        type: 'dayCenterDayNote',
        date: observation.lastUpdatedDate,
        data: observation,
      })),
      ...facilityEncounters.map((encounter): TimelineItem => ({
        key: encounter.reference.reference,
        type: 'facilityEncounter',
        date: encounter.lastUpdatedDate,
        data: encounter,
      })),
    ]

    // Group sequential document references that are within 5 minutes of each other
    return filterTimelineItemsByType(
      groupSequentialDocumentReferences(allItems),
      options
    )
  }, [
    validatedToolResponses,
    visitNoteTimelineItems,
    assessments,
    timelineNotes,
    phoneCalls,
    faxCommunications,
    orders,
    medicationRequests,
    standardTasks,
    recurringBookedAppointments,
    recurringCancelledAppointments,
    otherAppointments,
    documentReferences,
    dayCenterDayNotes,
    facilityEncounters,
    options,
  ])

  const timelineItemGroups = useMemo<TimelineItemGroups>(
    () =>
      orderBy(
        Object.entries(
          groupBy(timelineItems, (item) => toDateString(item.date))
        ).map(([dateString, items]) => ({
          dateString,
          items: orderBy(items, [(i) => i.date], ['desc']),
        })),
        [(o) => o.dateString],
        ['desc']
      ),
    [timelineItems]
  )

  const isLoading =
    isLoadingTools ||
    isLoadingAssessments ||
    isLoadingTimelineNotes ||
    isLoadingPhoneCalls ||
    isLoadingFaxes ||
    isLoadingOrders ||
    isLoadingMedications ||
    isLoadingTasks ||
    isLoadingAppointments ||
    isLoadingDocumentReferences ||
    isLoadingDayCenterDayNotes ||
    isLoadingFacilityEncounters

  return {
    timelineItems,
    timelineItemGroups,
    isLoading,
    isEmpty: isEmpty(timelineItems),
  }
}

function useVisitNoteTimelineItems(
  participantReference: Reference<Patient>
): TimelineItem[] {
  const { data: elationVisitNotes } = useSearch(
    VisitNote,
    VisitNote.elationVisitNotesQuery({
      patient: participantReference,
    })
  )

  const { data: participantNotes } = useSearch(
    ParticipantNote,
    ParticipantNote.queryByParticipant(participantReference)
  )

  const timelineItems = useMemo(() => {
    const signedParticipantNotes = participantNotes?.filter(
      (note) => !!note.signedOn
    ) as (ParticipantNote & { signedOn: string })[]
    return [
      ...(elationVisitNotes ?? []),
      ...(signedParticipantNotes ?? []),
    ].map((note): TimelineItem => ({
      key: note.reference.reference,
      type: 'visitNote',
      date:
        note instanceof VisitNote
          ? new Date(note.date ?? note.lastUpdated)
          : new Date(note.signedOn),
      data: note,
    }))
  }, [elationVisitNotes, participantNotes])
  return timelineItems
}
